import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"
import { SIDEBAR_ICON_SIZE } from "@/constants/UiConstants"
import {
  useFeatureFlag,
  useGetAllFeatureFlags,
} from "@/contexts/FeatureFlagContext"
import {
  firstEnabledTabPath,
  MC_BASE,
  STUDIOS as MC_STUDIO_CATALOG,
  STUDIO_ICONS_V2,
} from "@/features/material-compliance/studios"
import {
  type TranslationKeys,
  useTranslation,
} from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { IconShieldCheck } from "@tabler/icons-react"
import {
  ChevronRight,
  Database,
  Factory,
  Inbox,
  type LucideIcon,
} from "lucide-react"
import { useLocation, useNavigate } from "react-router"

interface SidebarStudioItem {
  labelKey: TranslationKeys
  icon: LucideIcon
  path: string
  to: string
}

// Legacy (SIDEBAR_V2 off): hardcoded three-studio array with the original
// click targets. Kept verbatim — do not change when adding V2 behavior.
const STUDIOS: SidebarStudioItem[] = [
  {
    labelKey: "MC_TAB_MASTER_DATA",
    icon: Database,
    path: `${MC_BASE}/master-data`,
    to: `${MC_BASE}/master-data`,
  },
  {
    labelKey: "MC_TAB_CUSTOMER_STUDIO",
    icon: Inbox,
    // `path` stays the bare studio segment so the prefix-match below covers
    // both the dashboard and requests routes; `to` is where a click actually
    // lands, since the bare path itself isn't a route.
    path: `${MC_BASE}/customer-studio`,
    to: `${MC_BASE}/customer-studio/requests`,
  },
  {
    labelKey: "MC_TAB_SUPPLIER_STUDIO",
    icon: Factory,
    path: `${MC_BASE}/supplier-studio`,
    to: `${MC_BASE}/supplier-studio`,
  },
]

/**
 * Sidebar entry for Material Compliance: an expandable group whose sub-items are
 * the studios. Collapsed (icon) mode hides the sub-items and shows the tooltip,
 * matching the rest of the sidebar.
 */
export function MaterialComplianceSidebarGroup({ title }: { title: string }) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const homeEnabled = useFeatureFlag("MC_HOME")
  const sidebarV2Enabled = useFeatureFlag("SIDEBAR_V2")
  const flags = useGetAllFeatureFlags()
  const { hasPermission } = useUser()

  const inSection = pathname === MC_BASE || pathname.startsWith(`${MC_BASE}/`)

  // SIDEBAR_V2: sub-items are catalog-driven — a studio appears if its own
  // flag is on AND at least ONE of its tabs' flag+permission passes. Using
  // studio-level `requiresPermission` alone would hide Customer Studio for a
  // member granted only Conformity (they have MC_CUSTOMER_DECLARATIONS_VIEW
  // but not the studio-level MC_QUESTIONNAIRES_VIEW). Click target = that
  // first-passing tab so they land on Conformity instead of a redirect-away
  // Dashboard. Kill-switch inherits via hasPermission (TEAM_MANAGEMENT off
  // → all pass).
  const v2SubItems: SidebarStudioItem[] = MC_STUDIO_CATALOG.filter((s) => {
    if (!flags[s.flag]) return false
    const anyTabPasses = s.tabs.some(
      (t) =>
        flags[t.flag] &&
        (!t.requiresPermission || hasPermission(t.requiresPermission)),
    )
    // A studio with zero tabs (none defined in the catalog) falls back to its
    // own studio-level requiresPermission; today every MC studio has tabs, so
    // this branch is defensive.
    if (s.tabs.length === 0) {
      return !s.requiresPermission || hasPermission(s.requiresPermission)
    }
    return anyTabPasses
  }).map((s) => ({
    labelKey: s.labelKey,
    icon: STUDIO_ICONS_V2[s.id] ?? s.icon,
    path: `${MC_BASE}/${s.id}`,
    to: firstEnabledTabPath(s, flags, hasPermission),
  }))

  const subItems = sidebarV2Enabled ? v2SubItems : STUDIOS

  // Header-row click target: under V2 the first ENABLED studio's landing tab
  // (a flag-disabled studio must never be the target); legacy stays hardcoded.
  const defaultPath = sidebarV2Enabled
    ? (v2SubItems[0]?.to ?? MC_BASE)
    : (STUDIOS[0]?.to ?? MC_BASE)

  return (
    <Collapsible
      asChild
      // The sidebar stays mounted across pages now; re-keying on entering or
      // leaving the section keeps the legacy group opening and closing with
      // it, as when every page mounted its own sidebar.
      key={sidebarV2Enabled ? "v2" : `in-section-${inSection}`}
      defaultOpen={sidebarV2Enabled ? true : inSection}
      className="group/mc"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            isActive={inSection}
            onClick={() => {
              if (homeEnabled) {
                if (pathname !== MC_BASE) navigate(MC_BASE)
                return
              }
              if (!inSection) navigate(defaultPath)
            }}
            tooltip={{
              children: title,
              side: "right",
              align: "center",
              className:
                "bg-secondary-foreground text-card shadow-md border border-muted-foreground",
            }}
          >
            <IconShieldCheck
              style={{ height: SIDEBAR_ICON_SIZE, width: SIDEBAR_ICON_SIZE }}
            />
            <span className="truncate">{title}</span>
            <ChevronRight className="ml-auto transition-transform group-data-[state=open]/mc:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {subItems.map((studio) => {
              const Icon = studio.icon
              return (
                <SidebarMenuSubItem key={studio.path}>
                  <SidebarMenuSubButton
                    asChild
                    isActive={pathname.startsWith(studio.path)}
                  >
                    <button type="button" onClick={() => navigate(studio.to)}>
                      {/* SIDEBAR_V2: sub-item icons match the 20px main-nav
                          icon size, overriding the sub-button's default 16px
                          ([&>svg]:size-4 in ui/sidebar.tsx). Legacy (flag off)
                          keeps the original unstyled 16px icon. */}
                      <Icon
                        style={
                          sidebarV2Enabled
                            ? {
                                height: SIDEBAR_ICON_SIZE,
                                width: SIDEBAR_ICON_SIZE,
                              }
                            : undefined
                        }
                      />
                      <span>{translate(studio.labelKey)}</span>
                    </button>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              )
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}
